import React, { useContext } from 'react';
import {
  ItemResult,
  ItemResultData,
} from 'js/components/controls/search-item-result';
import { SectionData } from 'js/components/controls/SelectInput/SectionData';
import {
  CommonSearchInput,
  CommonSearchInputHandle,
} from 'js/components/controls/CommonSearchInput';
import { SearchIcon } from 'js/components/controls/SearchIcon';
import { CmsCommonControls } from 'js/model/cms/cms-common-controls';
import { CmsBrowsePage, CmsFilters } from 'js/model/cms/cms-browse-page';
import { fetchTreatmentSearchResultsDebounced } from 'js/service/searchService';
import { NamedEntityOutput } from 'js/model/rainbow/NamedEntityOutput';
import {
  changeBrowseDataContext,
  AddLocation,
  browseRemovalParameterType,
} from 'js/pages/BrowsePage/BrowseData';
import { VenueListingCriteria } from 'js/model/rainbow/browse-page/VenueListingCriteria';
import { Aggregations } from 'js/model/rainbow/browse-page/Aggregations';
import { ClearableMode } from 'js/components/controls/DropdownInput/DropdownInput';
import { RequestData } from 'js/helpers/service';
import { debounceTrackMParticleSearch } from 'js/pages/tracking/trackSearch';
import { useDevice } from '@treatwell/ui';
import { buildItemResults, defaultResultItemKey } from './item-results';

interface Props {
  pageData: RequestData;
  cms: CmsBrowsePage;
  aggregations: Aggregations;
  treatments?: NamedEntityOutput[];
  treatmentType?: NamedEntityOutput;
  venueType?: NamedEntityOutput;

  /*
    The input will always be rendered, albeit sometimes not visible.
    It can't be conditionally rendered as that would mean that
    in componentDidMount the ref would not be available.

    If the need to use a ref can be eliminated then the input could
    probably be conditionally rendered.
   */
  visible: boolean;
  onFocus?: () => void;
}

export const TreatmentSearchInput = (props: Props) => {
  const debouncedSearch = fetchTreatmentSearchResultsDebounced(200);

  const { isMobile } = useDevice();

  const searchFunction = async (searchTerm: string): Promise<ItemResult[]> => {
    const results = await debouncedSearch(props.pageData, searchTerm);
    /**
     * Event must be tracked in 500ms after user stopped typing, to avoid registering too many irrelevant mid-state typing events.
     * Because we are awaiting for the request to complete, which is debounced after 200ms, we simply chain fire-and-forget
     * debounced tracking call with timeout of 300ms right after results request is completed.
     */
    if (results.length === 0) {
      debounceTrackMParticleSearch({
        country: props.pageData.channel.country.countryCode,
        isWebMobile: isMobile,
        triggerContext: 'browse',
        searchType: 'treatment',
        searchTerm,
        language: props.pageData.channel.languageCode,
      });
    }

    return results;
  };

  const searchInputRef = React.useRef<CommonSearchInputHandle>(null);
  const defaultResultKey = React.useRef<string | null>(undefined);
  const selectedResult = React.useRef<ItemResultData>(null);
  const changeBrowseData = useContext(changeBrowseDataContext);
  const {
    aggregations,
    treatments,
    treatmentType,
    venueType,
    cms,
    onFocus,
    visible,
  } = props;

  React.useEffect(() => {
    setSelectedItem();
  }, []);

  const setSelectedItem = (): void => {
    if (searchInputRef === null) {
      return;
    }

    defaultResultKey.current = defaultResultItemKey(
      aggregations,
      treatments || [],
      treatmentType,
      venueType
    );

    if (defaultResultKey.current) {
      searchInputRef.current?.setSelectedItemKey(defaultResultKey.current);
    }
  };

  const dataForNoSearch = (
    cmsFilters: CmsFilters
  ): SectionData<ItemResultData>[] => {
    const heading = cmsFilters['treatment-list-heading'];
    const items = buildItemResults(
      cmsFilters,
      aggregations,
      treatments || [],
      treatmentType,
      venueType
    );

    return [{ heading, items }];
  };

  const onBlur = () => {
    if (!selectedResult.current) {
      return;
    }

    const criteria: VenueListingCriteria = {};
    const removeParameters: browseRemovalParameterType[] = [
      browseRemovalParameterType.VENUETYPE,
      browseRemovalParameterType.TREATMENT,
      browseRemovalParameterType.TREATMENTTYPE,
    ];

    if (selectedResult.current.entityType === 'treatmentType') {
      criteria.treatmentCategoryGroupId = parseInt(
        selectedResult.current.entityValue,
        10
      );
    }

    if (selectedResult.current.entityType === 'treatments') {
      criteria.treatmentCategoryIds = selectedResult.current.entityValue
        .split(',')
        .map((id) => parseInt(id, 10));
    }

    if (selectedResult.current.entityType === 'venueType') {
      criteria.venueTypeId = parseInt(selectedResult.current.entityValue, 10);
    }

    changeBrowseData(
      criteria,
      removeParameters,
      AddLocation.CurrentPageWithRadius,
      undefined,
      undefined,
      selectedResult.current.aliasId
    );
  };

  const onChange = (
    value: string,
    selectedKey: string | null,
    data: ItemResultData | null
  ) => {
    if (selectedKey !== defaultResultKey.current) {
      selectedResult.current = data;
      defaultResultKey.current = selectedKey;
    } else {
      // The selected item is the same as the one originally defaulted to.
      selectedResult.current = null;
    }
  };

  const cmsFilters: CmsFilters = cms.page.browse.filters;
  const cmsCommonControls: CmsCommonControls = cms.common.controls;

  return (
    <CommonSearchInput
      cmsCommonControls={cmsCommonControls}
      ref={(ref) => {
        searchInputRef.current = ref;
      }}
      icon={SearchIcon}
      placeholder={cmsCommonControls.search['treatment-placeholder']}
      searchResultsHeading={
        cmsCommonControls.search['treatment-results-heading']
      }
      dataForNoSearch={dataForNoSearch(cmsFilters)}
      searchFunction={searchFunction}
      isErrorStyling={false}
      isHotJarWhiteList
      isFocusOnClear={false}
      isClearable={ClearableMode.ClearableWhenActive}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      renderHiddenResults={!visible}
      isPatternedBackground
    />
  );
};
